import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { Sparkle } from "../controls";
import { formatShortDate } from "../chiefComplaints/dateHelpers";
import HospitalisationService from "./HospitalisationService";
import { hospStatusBand } from "./data";
import { HOSP_V2_ADD, HOSP_V2_EDIT, HOSP_V2_EDIT_CAPTURED, HOSP_V2_REFRESH } from "./events";
import { MdChevronDown } from "../chiefComplaints/ccIcons";
import { PlusIcon, StethoscopeIcon, ToolbarPlusIcon } from "../labInvestigations/labIcons";
import ProceduresCell from "./ProceduresCell";
import MetaTooltip from "../medicines/MetaTooltip";

/**
 * Hospitalisation history — content column, backed by
 * api-hospitalisation-v2-service. Columns per the mock:
 *   Cause · Hosp. date · Discharge date · Duration · Procedures · Description · Status
 *
 * This column is read-only: both editing paths hand the work to the editor
 * rail. The cause and the status chip emit HOSP_V2_EDIT with the record (the
 * rail prefills Update, delete included); the toolbar and the empty state emit
 * HOSP_V2_ADD. No status-chip dropdown — the chip is a display badge and
 * status is edited in the rail's Status segment.
 */

// Same duration-formatter the rail editor shows under the discharge block,
// with the "Ongoing" case hoisted so a row with no admission date still reads
// as ongoing rather than "—".
const formatDuration = (fromISO, toISO, status) => {
  if (status === "hospitalised") {
    if (!fromISO) return "Ongoing";
    const from = new Date(fromISO);
    if (isNaN(from.getTime())) return "Ongoing";
    const days = Math.max(0, Math.floor((Date.now() - from.getTime()) / 86400000));
    return `${days} day${days === 1 ? "" : "s"} · ongoing`;
  }
  if (!fromISO || !toISO) return "—";
  const from = new Date(fromISO);
  const to = new Date(toISO);
  if (isNaN(from.getTime()) || isNaN(to.getTime())) return "—";
  const ms = to.getTime() - from.getTime();
  if (ms < 0) return "—";
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days < 30) return `${days} day${days === 1 ? "" : "s"}`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} mo${months === 1 ? "" : "s"}`;
  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  return remMonths === 0
    ? `${years} yr${years === 1 ? "" : "s"}`
    : `${years} yr ${remMonths} mo`;
};

// Cause display — the ICD code is rendered as a separate ` · ICD <code>` span
// alongside the name (mirrors the CC table's Complaint column). This helper
// returns only the readable name so the render can compose the two pieces
// with matching styling.
const causeName = (cause) => {
  if (!cause) return "—";
  const name = (cause.icdName || cause.description || "").trim();
  return name || (cause.icdCode || "").trim() || "—";
};

// The custom hover popover replaces the earlier native `title` — the browser
// tooltip could carry newlines but never real bullets, and dropped the CPT
// code styling. The popover component below (rendered inside every Procedure
// cell that has entries) is the sole source of the on-hover reveal.

const StatusChip = ({ status, onClick }) => {
  const band = hospStatusBand(status || "hospitalised");
  return (
    <button
      type="button"
      className="cc3-chip is-small hs3-chip"
      style={{ background: band.bg, color: band.fg }}
      onClick={onClick}
      title="Edit hospitalisation"
    >
      <span className="cc3-chip-dot" style={{ background: band.dot }} aria-hidden="true" />
      <span className="cc3-chip-label">{band.label}</span>
      <span className="cc3-chip-chev" aria-hidden="true"><MdChevronDown /></span>
    </button>
  );
};
StatusChip.propTypes = { status: PropTypes.string, onClick: PropTypes.func };

// Build a synthetic Hospitalisation-shaped row from a staged "add" captured
// item so it can slot into the same table as Hosp v2 rows. Mirrors CC/Dx/Med.
const stagedAddToHospRow = (item) => {
  const p = (item && item.payload) || {};
  const cptCode = p.cpt_code || "";
  const cptName = p.cpt_description || "";
  return {
    hospitalisationId: `staged-${item.id}`,
    cause: {
      icdCode:     "",
      icdName:     "",
      description: p.name || p.value || "(untitled)",
    },
    status:              p.date_of_discharge || String(p.status || "").toLowerCase() === "completed"
                            ? "discharged"
                            : "hospitalised",
    hospitalisationDate: p.date_of_surgery || p.since_date || null,
    dischargeDate:       p.date_of_discharge || null,
    procedures:          cptName
                            ? [{ code: cptCode, name: cptName, source: cptCode ? "catalog" : "free-text" }]
                            : [],
    description:         p.description || "",
    _captured:     item,
    _capturedIsAi: (item.confidence || 0) > 0,
    _stagedAdd:    true,
  };
};

const Hospitalisation = ({ patient, capturedItems }) => {
  const patientId = patient && (patient.id || patient.patientID);

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    const svc = new HospitalisationService();
    svc.list(
      patientId,
      (res) => {
        setDoc(res || { hospitalisations: [], version: 0 });
        setLoading(false);
      },
      (err) => {
        setError((err && err.message) || "Failed to load hospitalisation history");
        setLoading(false);
      }
    );
  }, [patientId]);

  useEffect(() => {
    load();
    EventService.addListener(HOSP_V2_REFRESH, load);
    return () => EventService.removeListener(HOSP_V2_REFRESH, load);
  }, [load]);

  const rowsRaw = useMemo(() => (doc && doc.hospitalisations) || [], [doc]);

  // Transcript-mode overlay. Adds slot in as new peach rows;
  // updates annotate the matching existing row with _captured so the
  // reviewer sees the peach highlight on the row they just modified.
  const inTranscriptMode = !!(capturedItems && capturedItems.length > 0);
  const rows = useMemo(() => {
    if (!inTranscriptMode) return rowsRaw;
    const stagedAdds = [];
    const updatesByEntryId = new Map();
    for (const it of capturedItems) {
      if (!it) continue;
      if (it.state !== "added_in_call" && it.state !== "edited_in_call") continue;
      if (it.operation === "add") stagedAdds.push(stagedAddToHospRow(it));
      else if (it.operation === "update" && it.targetEntryId) {
        updatesByEntryId.set(String(it.targetEntryId), it);
      }
    }
    // Overlay staged payload fields onto the base row so the table
    // columns (Cause / dates / status / procedures / description) show
    // what the reviewer just saved, not the untouched Hosp v2 record.
    const annotated = rowsRaw.map((row) => {
      const update = updatesByEntryId.get(String(row.hospitalisationId));
      if (!update) return row;
      const p = (update.payload) || {};
      const statusMapped = p.status === "completed" ? "discharged"
        : p.status === "ongoing" ? "hospitalised"
        : row.status;
      const procs = p.cpt_code
        ? [{ code: p.cpt_code, name: p.cpt_description || "", source: "catalog" }]
        : row.procedures;
      return {
        ...row,
        cause: {
          ...(row.cause || {}),
          icdCode:     p.icd_code        || (row.cause && row.cause.icdCode) || "",
          icdName:     p.icd_description || (row.cause && row.cause.icdName) || "",
          description: p.name            || (row.cause && row.cause.description) || "",
        },
        hospitalisationDate: p.date_of_surgery   || row.hospitalisationDate,
        dischargeDate:       p.date_of_discharge || row.dischargeDate,
        status:              statusMapped,
        procedures:          procs,
        description:         p.description !== undefined ? p.description : row.description,
        _captured:           update,
        _capturedIsAi:       (update.confidence || 0) > 0,
      };
    });
    return [...annotated, ...stagedAdds];
  }, [rowsRaw, capturedItems, inTranscriptMode]);

  /** Hand the record to the editor rail, which opens prefilled in Update. */
  // Transcript-mode captured rows route to a re-edit event so the modal
  // can pop the AI proposal back open as an editable card rather than
  // opening the standalone edit sheet in the patient-page rail.
  const openEdit = (h) => {
    if (h && h._captured) {
      EventService.emit(HOSP_V2_EDIT_CAPTURED, {
        captured: h._captured,
        hospv2Record: h._stagedAdd ? null : h,
      });
      return;
    }
    EventService.emit(HOSP_V2_EDIT, { hospitalisation: h });
  };

  if (!patientId) {
    return <div className="ph-slot-empty"><p>Waiting for patient…</p></div>;
  }
  if (loading && !doc) {
    return <div className="ph-slot-empty"><p>Loading hospitalisation history…</p></div>;
  }
  if (error) {
    return (
      <div className="ph-slot-empty">
        <p>{error}</p>
        <button type="button" className="sec-btn sec-btn-ghost" onClick={load}>Retry</button>
      </div>
    );
  }

  return (
    <div className="cc3-card">
      <header className="cc3-toolbar">
        <h2 className="cc3-title">Hospitalisation history</h2>
        <span className="cc3-count">
          &middot; {rows.length === 0 ? "No entries" : `${rows.length} ${rows.length === 1 ? "entry" : "entries"}`}
        </span>
        <button
          type="button"
          className="cc3-add"
          onClick={() => EventService.emit(HOSP_V2_ADD)}
        >
          <ToolbarPlusIcon /> Add hospitalisation
        </button>
      </header>

      {rows.length === 0 ? (
        /* Empty state (3097:2667): the table head STAYS; the body is the
           stethoscope medallion + copy + one filled add button. */
        <div className="hs3-table" role="table">
          <div className="hs3-thead" role="row">
            <span role="columnheader">Cause of hospitalisation</span>
            <span role="columnheader">Hosp. date</span>
            <span role="columnheader">Discharge date</span>
            <span role="columnheader">Duration</span>
            <span role="columnheader">Procedures</span>
            <span role="columnheader">Description</span>
            <span role="columnheader">Status</span>
          </div>
          <div className="li3-empty">
            <span className="li3-empty-medallion" aria-hidden="true"><StethoscopeIcon /></span>
            <p className="li3-empty-title">No hospitalisations recorded</p>
            <p className="li3-empty-hint">
              Nothing added yet. Add the patient&rsquo;s first hospitalisation to begin
              building their history.
            </p>
            <div className="li3-method-btns">
              <button
                type="button"
                className="li3-btn-digitise"
                onClick={() => EventService.emit(HOSP_V2_ADD)}
              >
                <PlusIcon /> Add hospitalisation
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="hs3-table" role="table">
          <div className="hs3-thead" role="row">
            <span role="columnheader">Cause of hospitalisation</span>
            <span role="columnheader">Hosp. date</span>
            <span role="columnheader">Discharge date</span>
            <span role="columnheader">Duration</span>
            <span role="columnheader">Procedures</span>
            <span role="columnheader">Description</span>
            <span role="columnheader">Status</span>
          </div>

          {rows.map((h) => (
            <div
              className={`hs3-row${h._captured ? " is-captured" : ""}`}
              role="row"
              key={h.hospitalisationId}
            >
              <span className="hs3-cell hs3-c-name" role="cell">
                {h._capturedIsAi ? (
                  <span className="cc-spark" aria-label="AI captured" title="AI captured"><Sparkle aria-hidden /></span>
                ) : null}
                <button
                  type="button"
                  className="hs3-name-btn"
                  onClick={() => openEdit(h)}
                  title="Edit hospitalisation"
                >
                  {causeName(h.cause)}
                  {h.cause && h.cause.icdCode ? (
                    <span className="cc-icd-inline"> &middot; ICD {h.cause.icdCode}</span>
                  ) : null}
                </button>
              </span>
              <span className="hs3-cell hs3-c-date" role="cell">
                {h.hospitalisationDate ? formatShortDate(h.hospitalisationDate) : "—"}
              </span>
              <span className="hs3-cell hs3-c-date" role="cell">
                {h.dischargeDate ? formatShortDate(h.dischargeDate) : "—"}
              </span>
              <span className="hs3-cell hs3-c-date" role="cell">
                {formatDuration(h.hospitalisationDate, h.dischargeDate, h.status)}
              </span>
              {/* First 3 procedures inline; overflow / mid-item
                  truncation surface a button that opens the full-list
                  dialog. See ProceduresCell for the rules. */}
              <span className="hs3-cell hs3-c-soft hs3-c-procs" role="cell">
                <ProceduresCell procedures={h.procedures} />
              </span>
              {/* Description clamps at 4 lines; MetaTooltip surfaces the
                  full text in a white popover on hover (only when
                  actually truncated). Native `title` dropped so the
                  tooltip appearance stays consistent with the rest of
                  the fleet. */}
              <span className="hs3-cell hs3-c-soft hs3-c-desc" role="cell">
                <MetaTooltip text={h.description} lines={4} />
              </span>
              <span className="hs3-cell" role="cell">
                <StatusChip status={h.status} onClick={() => openEdit(h)} />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

Hospitalisation.propTypes = {
  patient: PropTypes.object,
  /**
   * Transcript-mode overlay. When populated, staged captured items for
   * `hospitalisation` are merged into the table (peach highlight +
   * sparkle for AI-touched rows). Empty / unpassed = standalone
   * behaviour. Wave A first-cut merges only staged ADDS.
   */
  capturedItems: PropTypes.array,
};

Hospitalisation.defaultProps = {
  capturedItems: [],
};

export default Hospitalisation;
